<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { PASSWORD_MIN_LENGTH, type PasswordRule } from '@/composables/usePasswordRules'

/** Presentational checklist: each rule turns from grey to Smerra orange (with a
 *  check) as it is satisfied. Labels come from the shared `auth.passwordRules`
 *  namespace. Logic lives in `usePasswordRules`. */
defineProps<{ checklist: PasswordRule[] }>()

const { t } = useI18n()
</script>

<template>
  <ul class="pwrules" aria-live="polite">
    <li
      v-for="rule in checklist"
      :key="rule.key"
      class="pwrules__item"
      :class="{ 'pwrules__item--met': rule.met }"
    >
      <span class="pwrules__icon" aria-hidden="true">
        <svg v-if="rule.met" viewBox="0 0 16 16" width="16" height="16">
          <circle cx="8" cy="8" r="7.25" fill="currentColor" />
          <path d="M4.5 8.2l2.2 2.2 4.8-4.8" fill="none" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <svg v-else viewBox="0 0 16 16" width="16" height="16">
          <circle cx="8" cy="8" r="7.25" fill="none" stroke="currentColor" stroke-width="1.4" />
        </svg>
      </span>
      <span class="pwrules__label">{{ t(`auth.passwordRules.${rule.key}`, { min: PASSWORD_MIN_LENGTH }) }}</span>
    </li>
  </ul>
</template>

<style scoped>
/* Suivi des règles de complexité : gris par défaut → orange Smerra une fois remplie. */
.pwrules {
  list-style: none;
  margin: -0.25rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.pwrules__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  color: var(--color-gray-700);
  transition: color var(--dur-fast) var(--ease-out);
}
.pwrules__icon {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  color: var(--color-gray-500);
  transition: color var(--dur-fast) var(--ease-out);
}
.pwrules__item--met {
  color: var(--success);
}
.pwrules__item--met .pwrules__icon {
  color: var(--success);
}
</style>
